import type { LocalDate } from '@saay/formatting';

import { AdminSolarDateField } from './admin-date-field.js';
import { ListSelect } from './list-filters.js';
import { addCalendarDays, tehranToday } from '../validation.js';

export type DatePreset = '' | 'today' | 'last-7-days' | 'last-30-days' | 'this-month' | 'custom';

const DATE_PRESET_OPTIONS = [
  { label: 'امروز', value: 'today' },
  { label: '۷ روز اخیر', value: 'last-7-days' },
  { label: '۳۰ روز اخیر', value: 'last-30-days' },
  { label: 'این ماه', value: 'this-month' },
  { label: 'بازهٔ دلخواه', value: 'custom' },
] as const;

export function dateRangeForPreset(
  preset: Exclude<DatePreset, 'custom'>,
  today: LocalDate = tehranToday(),
): { readonly from: LocalDate; readonly to: LocalDate } {
  switch (preset) {
    case '':
      return { from: '', to: '' };
    case 'today':
      return { from: today, to: today };
    case 'last-7-days':
      return { from: addCalendarDays(today, -6), to: today };
    case 'last-30-days':
      return { from: addCalendarDays(today, -29), to: today };
    case 'this-month':
      return { from: `${today.slice(0, 7)}-01` as LocalDate, to: today };
  }
}

export function datePresetForRange(
  from: LocalDate,
  to: LocalDate,
  today: LocalDate = tehranToday(),
): DatePreset {
  for (const preset of ['today', 'last-7-days', 'last-30-days', 'this-month'] as const) {
    const range = dateRangeForPreset(preset, today);
    if (from === range.from && to === range.to) return preset;
  }
  return from === '' && to === '' ? '' : 'custom';
}

export function DatePresetSelect({
  onChange,
  value,
}: {
  readonly onChange: (value: DatePreset) => void;
  readonly value: DatePreset;
}) {
  return (
    <ListSelect
      label="بازهٔ زمانی"
      onChange={(next) => onChange(next as DatePreset)}
      options={DATE_PRESET_OPTIONS}
      placeholder="همه زمان‌ها"
      value={value}
    />
  );
}

export function CustomDateRangeFields({
  from,
  namePrefix,
  onFromChange,
  onToChange,
  to,
}: {
  readonly from: LocalDate;
  readonly namePrefix: string;
  readonly onFromChange: (value: LocalDate) => void;
  readonly onToChange: (value: LocalDate) => void;
  readonly to: LocalDate;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <AdminSolarDateField
        label="از تاریخ"
        name={`${namePrefix}-from`}
        onChange={onFromChange}
        value={from}
      />
      <AdminSolarDateField
        label="تا تاریخ"
        name={`${namePrefix}-to`}
        onChange={onToChange}
        value={to}
      />
    </div>
  );
}
